
import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { toast } from 'react-toastify'
import downloadIcon from '../../../assets/images/download-icon.svg'
import sidebarItems from '../const'
import { RootState } from '../../../redux/store'
import { getAccount, getUsersWithAttempts } from '../../../libs/services'
import { PageTitle, LoadingSpinner } from '../../../components/atoms'
import { Institution, User } from '../../../models/global.types'
import { useAuth } from '../../../hooks/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { setInstitution } from '../../../redux/slices/institutionSlice'
import { DateUtils, logger } from '../../../utils'
import { StatusValue } from '../../../enums/enums'

/**
 * Internal interface - used to show correct data in the downloaded
 * Excel sheet.
 */
interface Student {
  'Student Name': string
  'Student Email': string
  Status: string
  'Date of Completion'?: string
}

const StudentsList: FC = () => {
  const { pageType } = useParams()

  // Set up screen title based on the selected page in the side bar.  Default to all assigned students:
  const selectedSideBar = sidebarItems.find(sidebarItem => sidebarItem.slot.toLowerCase() === pageType?.toLowerCase())
  const title: string = selectedSideBar !== undefined ? selectedSideBar.title : sidebarItems[0].title

  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [excelFileName, setExcelFileName] = useState<string>('student-list')
  const { userProfile } = useAuth()
  const { institution } = useSelector((state: RootState) => state.institution)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const convertUsersToStudents = useCallback((u: User[]): Student [] => {
    const converted = u.map((user: User, _index) => {
      const attemptStatus: string = user.attemptData.status
      const status = attemptStatus.slice(0, 1)[0].toUpperCase() + attemptStatus.slice(1)
      const student: Student = {
        'Student Name': `${user.lastName}, ${user.firstName}`,
        'Student Email': user.email,
        Status: status
      }
      // Dynamically add Date of Completion property if we're on the right tab.
      if (title.toLowerCase() === 'completed students' &&
        user.attemptData.status !== null &&
         user.attemptData.status.toLowerCase() === 'completed' &&
        user.attemptData.attempts.length > 0
      ) {
        student['Date of Completion'] = new Date(user.attemptData.attempts[0].endedAt).toDateString()
      }
      return student
    })
    return converted
  }, [title])
  /**
   * Fetches fresh student data.
   * @returns An array of Students.
   */
  const fetchUsers = useCallback(async (): Promise<Student[]> => {
    let response

    switch (title?.toLowerCase()) {
      case 'in progress students':
        response = await getUsersWithAttempts(undefined, StatusValue.INPROGRESS) // enum for status, model for attempts
        break

      case 'completed students':
        response = await getUsersWithAttempts(true, StatusValue.COMPLETED)
        break

      case 'pending students':
        response = await getUsersWithAttempts(undefined, StatusValue.PENDING)
        break

      default: {
        response = await getUsersWithAttempts()
        break
      }
    }
    // Access the users from the data property
    const studentList = convertUsersToStudents(response.data.users)
    return studentList
  }, [convertUsersToStudents, title])

  /**
   * Get the Institution to fill out the Excel file name if downloaded.
   */
  const fetchInstitution = useCallback(async (): Promise<Institution> => {
    if (institution === null || institution === undefined) {
      const response = await getAccount()
      dispatch(setInstitution(response.data.account))
      return response.data.account
    } else {
      return institution
    }
  }, [dispatch, institution])

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const [institutionData, studentData] = await Promise.all([fetchInstitution(), fetchUsers()])
      setStudents(studentData)
      setExcelFileName(`${institutionData.name} ${title} ${DateUtils.formatDateToMDY(new Date())}`)
    } catch (error) {
      const err = error as Error
      logger.error(err)
      toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
    }

    setLoading(false)
  }, [fetchInstitution, fetchUsers, title])

  /**
   * Only allow admins to view page.
   */
  useEffect(() => {
    if (userProfile?.role.slug !== 'admin') {
      navigate('/user-dashboard')
    }
  }, [navigate, userProfile])

  /**
   * Fetch a new set of data whenever the "title" changes, meaning:
   * 1. On initial page load.
   * 2. Whenever a user clicks on the sidebar.
   */
  useEffect(() => {
    setLoading(true)
    void fetchData()
  }, [title, fetchData])

  /**
   * Fires when user downloads the excel doc.
   */
  const handleDownloadExcelButtonClick = (): void => {
    try {
      const fileName = excelFileName.replaceAll(' ', '_')
      const worksheet = XLSX.utils.json_to_sheet(students)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, title)
      XLSX.writeFile(workbook, `${fileName}.xlsx`)
    } catch (error) {
      const err = error as Error
      logger.error(err)
      toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
    }
  }

  if (!loading) {
    return (
      <div className='flex flex-col'>
        <PageTitle title={title} addScreenReaderH1={false} />
        <div className='flex justify-between px-[45px] py-[51px] items-center'>
          <h1 className='text-[72px] font-bold text-[rgba(52,232,158,0.75)]'>{title}</h1>
          <button onClick={handleDownloadExcelButtonClick}>
            <img src={downloadIcon} className='w-[36px] cursor-pointer' alt='download icon' />
          </button>
        </div>
        <div className='pl-[29px] pr-[10px]'>
          <div className='pr-[8px] overflow-y-auto flex-1 scrollbar' style={{ maxHeight: 'calc(100vh - 300px)' }}>
            <table className='table-auto text-left w-full'>
              <thead className='text-dopage-lightGreen font-bold text-[18px]'>
                <tr className='h-[42px]'>
                  <th>Student Name</th>
                  <th>Student Email</th>
                  <th>Status</th>
                  {title.toLowerCase() === 'completed students' ? <td>Date of Completion</td> : null}
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  return (
                    <tr key={index} className='text-dopage-lightGreen text-[18px] border-t border-[#091F28] h-[42px] leading-[22px]'>
                      <td>{student['Student Name']}</td>
                      <td>{student['Student Email']}</td>
                      <td>{student.Status}</td>
                      <td>{title.toLowerCase() === 'completed students' ? student['Date of Completion'] : null}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  } else {
    return <div className='h-[100%] flex justify-center items-center'><LoadingSpinner /></div>
  }
}

export default StudentsList
